import { useMutation, useQuery, useQueryClient, useSuspenseQuery } from '@tanstack/react-query';
import {
  formatKhisPeriod,
  lastCompleteKhisPeriod,
  monthOf,
  QUALITY_INDICATORS,
} from '@wodi/domain';
import type { QualityIndicatorView } from '@wodi/shared';
import {
  Button,
  Cell,
  Column,
  Dialog,
  DialogActions,
  LoadingBlock,
  Notice,
  PageHeader,
  Row,
  Section,
  Select,
  Table,
  TableBody,
  TableHeader,
  TextField,
  TrendChart,
} from '@wodi/ui';
import { useState } from 'react';

import { qualityDashboardQuery, qualityKeys, setQualityTarget } from '../api/khis.ts';
import { can, sessionQuery } from '../api/session.ts';
import { useOnline } from '../hooks/use-online.ts';
import { TableScroll } from '../layout/screen.tsx';
import { errorText, LoadFailed, periodWords, recentPeriods, whenWords } from '../khis/format.tsx';
import { BackToReports } from '../reports/report-frame.tsx';
import { toasts } from '../toasts.ts';
import styles from './quality.module.css';
import { monthSentence, targetSentence, valueLabel, valueWords } from './words.ts';

// Reports > Quality indicators (docs/plans/phase3-khis-and-quality.md §1.2,
// docs/workflows/quality-indicators.md): one written line per indicator, its definition on
// screen, the facility's own target if set, and a six-month chart with a table view. Not a
// grid of KPI cards (DESIGN.md §11). No target is built in: each is the facility's setting.

const MONTHS_OFFERED = 12;

function TargetDialog({
  indicator,
  period,
  onClose,
}: {
  indicator: QualityIndicatorView;
  period: string;
  onClose: () => void;
}) {
  const queryClient = useQueryClient();
  const online = useOnline();
  const [text, setText] = useState(indicator.target === null ? '' : String(indicator.target));
  const parsed = text.trim() === '' ? null : Number(text);
  const valid = parsed === null || (Number.isFinite(parsed) && parsed >= 0);
  const save = useMutation({
    mutationFn: (target: number | null) => setQualityTarget(indicator.key, target),
    onSuccess: async (_, target) => {
      await queryClient.invalidateQueries({ queryKey: qualityKeys.dashboard(period) });
      toasts.show({ title: target === null ? 'Target cleared' : 'Target saved' });
      onClose();
    },
  });
  const unit = QUALITY_INDICATORS[indicator.key].unit;
  return (
    <Dialog
      title={`Target for ${QUALITY_INDICATORS[indicator.key].title.toLowerCase()}`}
      isOpen
      onOpenChange={(open) => {
        if (!open) onClose();
      }}
    >
      <form
        onSubmit={(e) => {
          e.preventDefault();
          if (valid) save.mutate(parsed);
        }}
      >
        <p className={styles.prose}>
          {`Your facility's own target, ${QUALITY_INDICATORS[indicator.key].better === 'lower' ? 'met at or below' : 'met at or above'} the value. Wodi sets none. Leave it empty to clear it.`}
        </p>
        <TextField
          label="Target"
          unit={unit === 'percent' ? '%' : unit === 'minutes' ? 'min' : unit}
          width="sm"
          inputMode="decimal"
          value={text}
          onChange={setText}
          {...(valid ? {} : { errorMessage: 'Enter a number of 0 or more, or leave it empty.' })}
        />
        {save.error === null ? null : (
          <Notice tone="error">
            {errorText(save.error, 'The target was not saved. Try again.')}
          </Notice>
        )}
        <DialogActions>
          <Button variant="quiet" onPress={onClose}>
            Cancel
          </Button>
          <Button type="submit" variant="primary" isDisabled={!online || !valid || save.isPending}>
            Save target
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
}

function Indicator({
  indicator,
  period,
  canSetTarget,
}: {
  indicator: QualityIndicatorView;
  period: string;
  canSetTarget: boolean;
}) {
  const [editing, setEditing] = useState(false);
  const definition = QUALITY_INDICATORS[indicator.key];
  const latest = indicator.months.at(-1);
  return (
    <Section
      title={definition.title}
      actions={
        canSetTarget ? (
          <Button
            dense
            onPress={() => {
              setEditing(true);
            }}
          >
            Set target
          </Button>
        ) : null
      }
    >
      {latest === undefined ? null : (
        <p className={styles.prose}>{monthSentence(indicator.key, latest)}</p>
      )}
      <p className={styles.meta}>{targetSentence(indicator)}</p>
      <details className={styles.definition}>
        <summary>How this is counted</summary>
        <p className={styles.prose}>{definition.definition}</p>
      </details>
      {definition.perMonth ? (
        <TrendChart
          title={`${definition.title}, last ${String(indicator.months.length)} months`}
          valueLabel={valueLabel(indicator.key)}
          points={indicator.months.map((m) => ({
            label: periodWords(m.period),
            value: m.value,
            valueText: valueWords(indicator.key, m.value),
          }))}
        />
      ) : null}
      {editing ? (
        <TargetDialog
          indicator={indicator}
          period={period}
          onClose={() => {
            setEditing(false);
          }}
        />
      ) : null}
    </Section>
  );
}

function Overview({ indicators }: { indicators: readonly QualityIndicatorView[] }) {
  return (
    <Section
      title="All indicators"
      description="This month beside the one before, with the facility's own targets."
    >
      <TableScroll>
        <Table flat aria-label="All indicators">
          <TableHeader>
            <Column isRowHeader>Indicator</Column>
            <Column>This month</Column>
            <Column>Month before</Column>
            <Column>Target</Column>
          </TableHeader>
          <TableBody items={indicators.map((i) => ({ ...i, id: i.key }))}>
            {(i) => {
              const latest = i.months.at(-1);
              const before = i.months.at(-2);
              return (
                <Row id={i.id}>
                  <Cell>{QUALITY_INDICATORS[i.key].title}</Cell>
                  <Cell>{valueWords(i.key, latest?.value ?? null)}</Cell>
                  <Cell>
                    {QUALITY_INDICATORS[i.key].perMonth
                      ? valueWords(i.key, before?.value ?? null)
                      : 'Not kept per month'}
                  </Cell>
                  <Cell>{targetSentence(i)}</Cell>
                </Row>
              );
            }}
          </TableBody>
        </Table>
      </TableScroll>
    </Section>
  );
}

export function QualityPage() {
  const { data: session } = useSuspenseQuery(sessionQuery);
  const allowed = session !== null && can(session, 'quality.read');
  const now = new Date();
  const current = formatKhisPeriod(monthOf(now));
  const [period, setPeriod] = useState(formatKhisPeriod(lastCompleteKhisPeriod(now)));
  const dashboard = useQuery({ ...qualityDashboardQuery(period), enabled: allowed });
  const online = useOnline();
  const data = dashboard.data;
  return (
    <div className={styles.page}>
      <BackToReports />
      <PageHeader
        title="Quality indicators"
        description="How the facility is doing, month by month, from what Wodi already records."
      />
      {session === null || !allowed ? (
        <Notice tone="info">
          You don&apos;t have permission to see the quality indicators. Ask an administrator if you
          need it.
        </Notice>
      ) : (
        <>
          <div className={styles.filters}>
            <Select
              label="Month"
              width="sm"
              options={recentPeriods(current, MONTHS_OFFERED).map((p) => ({
                id: p,
                label: p === current ? `${periodWords(p)}, so far` : periodWords(p),
              }))}
              value={period}
              onChange={(key) => {
                if (key !== null) setPeriod(String(key));
              }}
            />
          </div>
          {online ? null : (
            <Notice tone="info">
              You&apos;re offline. The indicators shown are what was last loaded and may be out of
              date.
            </Notice>
          )}
          {dashboard.isPending ? <LoadingBlock label="Loading the indicators" rows={6} /> : null}
          {dashboard.isError ? (
            <LoadFailed
              error={dashboard.error}
              what="The indicators"
              retry={() => void dashboard.refetch()}
            />
          ) : null}
          {data === undefined ? null : (
            <>
              <p className={styles.meta}>
                {`${data.facility.name}, ${periodWords(data.period)}. As at ${whenWords(data.dataAsAt)}.`}
              </p>
              <Overview indicators={data.indicators} />
              {data.indicators.map((i) => (
                <Indicator
                  key={i.key}
                  indicator={i}
                  period={period}
                  canSetTarget={can(session, 'quality.settings.manage')}
                />
              ))}
            </>
          )}
        </>
      )}
    </div>
  );
}
